<template>
  <div>
    <div class="seeMore pt-3">
      <p class="grey--text">{{ catchPhrase }}</p>
      <div
        class="primary--text"
        @click="
          isUniverse && !isOther && collection ? goTo(collection.id) : goTo()
        "
      >
        {{ $t('cp_wsuggestioncard_see_more') }}
        <v-icon color="primary" small>mdi-arrow-right</v-icon>
      </div>
    </div>

    <CSuggestedCarousel
      v-if="isDesktopPage"
      :contents="formattedContents"
      @slide="handleSlide"
      @launch="launchContent"
    />

    <div v-if="!isDesktopPage" class="scroll-container">
      <v-card
        v-for="(content, index) in isOther ? otherContents : contents"
        :key="index"
        flat
        class="grey lighten-3 fixed-size-card"
        @click="launchContent(content)"
      >
        <v-badge
          v-if="content.isSuggested"
          color="primary"
          class="suggested-chip"
        >
          <template #badge>
            <v-icon color="white">mdi-pin</v-icon>
          </template>
        </v-badge>
        <div>
          <v-img
            v-if="content.metadatas.thumbnail"
            :lazy-src="getCaption(content.metadatas.thumbnail).lazySrc"
            :src="getCaption(content.metadatas.thumbnail).cdnSrc"
            aspect-ratio="1"
            class="ma-2 img-card"
          ></v-img>
          <v-img
            v-else
            :lazy-src="getCaption(content.metadatas.cards[0]).lazySrc"
            :src="getCaption(content.metadatas.cards[0]).cdnSrc"
            aspect-ratio="1"
            class="ma-2 img-card"
          ></v-img>
          <p class="mx-2 font-weight-bold card-title">{{ content.name }}</p>
        </div>
        <div class="contentMedatas ma-2">
          <p
            class="contentMedatas-publishedAt grey--text text--darken-2 white my-auto"
          >
            {{ formatDate(content.publishedAt) }}
          </p>
          <div
            v-if="
              content.metadatas.audios && content.metadatas.audios.length != 0
            "
            class="contentMedatas-duration grey--text text--darken-2 d-flex"
          >
            <v-icon small class="mr-1">mdi-timer-outline</v-icon>
            <p class="my-auto">
              {{
                formatTime(
                  content.metadatas.audios.reduce(
                    (sum, audio) => sum + audio.metadatas.duration,
                    0
                  )
                )
              }}
            </p>
          </div>
          <div
            v-else-if="
              content.metadatas.videos && content.metadatas.videos.length != 0
            "
            class="caption grey--text d-flex"
          >
            <v-icon small class="mr-1">mdi-timer-outline</v-icon>
            <p class="my-auto">
              {{ formatTime(content.metadatas.videos[0].metadatas.duration) }}
            </p>
          </div>
        </div>
        <div v-if="isOther" class="mt-1 mx-3 d-block">
          <p class="tag-name white--text text-xs-center">
            {{ content.tagName }}
          </p>
        </div>
      </v-card>
    </div>
  </div>
</template>

<script>
import $clone from 'lodash.clonedeep'
import { mapGetters } from 'vuex'
import CSuggestedCarousel from '../../excentrics/shells/bb-default/components/CSuggestedCarousel'
import formatDate from '~/mixins/formatDate'
import formatTime from '~/mixins/formatTime'
import { getCaption } from '~/@mediam/spoke-js/utils/getters'
import ContentModel from '~/@mediam/spoke-js/models/brocoli/Content'
import spoke from '~/utils/spoke'

export default {
  components: {
    CSuggestedCarousel,
  },
  mixins: [formatTime, formatDate],
  props: {
    catchPhrase: {
      type: String,
      required: true,
    },
    isOther: {
      type: Boolean,
      required: false,
    },
    isDesktopPage: {
      type: Boolean,
      required: false,
    },
    suggestedContentLimit: {
      type: Number,
      required: false,
      default: 3,
    },
  },
  data() {
    return {
      suggestedContents: this.$store.state.player.content.suggestedContents,
      collection: null,
      contents: [],
      otherContents: [],
      isUniverse: this.$voicer.isUniverse,
      collectionsIds: this.$voicer.collections
        ? this.$voicer.collections.map((collection) => collection.tagId)
        : [],
      collections: this.$voicer.collections,
    }
  },
  computed: {
    formattedContents() {
      return this.contents.map((content) => new ContentModel(content).data)
    },
    isActiveAutoplay() {
      return JSON.parse(this.$store.state.player.autoplay)
    },
    ...mapGetters({
      tags: 'filters/categories',
    }),
  },
  watch: {
    $route(to, from) {
      if (this.contents && this.contents.length) {
        this.fetchDatas()
      }
    },
    ...mapGetters({
      tags: 'filters/categories',
    }),
  },
  mounted() {
    this.fetchDatas()
  },
  methods: {
    handleSlide(data) {
      const { currentSlide } = data

      this.activeContent = $clone(new ContentModel(this.contents[currentSlide]))
    },
    getCaption(meta) {
      return getCaption(meta)
    },
    goTo(collectionId) {
      if (collectionId) {
        this.$router.push({
          path: `/universe/${collectionId}`,
        })
      } else {
        this.$router.push({ path: '/' })
      }
      this.$emit('close')
    },
    async fetchDatas() {
      this.autoplayContent = null
      this.suggestedContents =
        spoke.mediaplayer.episode?.content.data.suggestedContents
      if (this.isUniverse) {
        this.collection = this.getContentCollection()
        await Promise.all([
          this.getOldContents(this.collection),
          this.getOtherCollectionsContents(this.collection),
        ])
      } else {
        await this.getOldContents()
      }
    },
    launchContent(content) {
      this.$emit('close')
      this.$store.dispatch('player/changePlayerContent', {
        content: new ContentModel(content).data,
        noRedirect: false,
      })
      if (this.autoplayContent.type !== 'video' || content.type !== 'video') {
        this.$store.dispatch('player/ctrlPlayer', {
          action: 'play',
          content: new ContentModel(content).data,
        })
      }
    },
    getContentCollection() {
      const contentTagNames = spoke.mediaplayer.episode.content.data.tags
      let contentCollection
      this.tags.forEach((parentTag) => {
        const matchingTag = parentTag.tags.find((tag) => {
          return (
            contentTagNames.includes(tag.name) &&
            this.collectionsIds.includes(tag.id)
          )
        })
        if (matchingTag) {
          contentCollection = matchingTag
        }
      })
      return contentCollection
    },
    async getOldContents(tag) {
      this.contents = []
      const tmpSuggested = []
      if (this.suggestedContents && tag) {
        tmpSuggested.push(
          ...this.suggestedContents.filter((content) =>
            content.tags?.includes(tag.name)
          )
        )
      } else if (this.suggestedContents) {
        tmpSuggested.push(...this.suggestedContents)
      }
      if (tmpSuggested.length) {
        this.autoplayContent = tmpSuggested[0]
        this.$emit('content', tmpSuggested[0])
        this.contents = tmpSuggested.map((content) => ({
          ...content,
          isSuggested: true,
        }))
      }
      const limit = this.suggestedContentLimit - tmpSuggested.length
      if (limit > 0) {
        const currentPublishedAt = new Date(
          spoke.mediaplayer.episode.content.data.publishedAt
        )
        currentPublishedAt.setSeconds(currentPublishedAt.getSeconds() - 10)
        const params = {
          limit,
          toDate: currentPublishedAt.toISOString(),
          is: 'podcast',
        }
        if (tag) {
          params.tags = [tag.id]
        }
        const response = await spoke.http.get(`/contents`, {
          params,
        })
        this.contents = this.contents.concat(response.items)

        if (!this.autoplayContent) {
          this.autoplayContent = this.contents[0]
          this.$emit('content', this.contents[0])
        }
      }

      if (this.contents.length < 3) {
        this.getRecentContents(tag)
      }
    },
    async getRecentContents(tag) {
      const limit = 3 - this.contents.length
      const currentPublishedAt = new Date(
        spoke.mediaplayer.episode.content.data.publishedAt
      )
      currentPublishedAt.setSeconds(currentPublishedAt.getSeconds() + 10)
      if (limit > 0) {
        const params = {
          limit,
          fromDate: currentPublishedAt.toISOString(),
          is: 'podcast',
        }
        if (tag) {
          params.tags = [tag.id]
        }
        const response = await spoke.http.get(`/contents`, {
          params,
        })
        if (!this.autoplayContent && response.items.length) {
          this.autoplayContent = response.items[0]
          this.$emit('content', response.items[0])
        }
        this.contents = this.contents.concat(response.items)
      }
    },
    async getOtherCollectionsContents(contentCollection) {
      this.otherContents = []
      let suggestedCollectionIds = []
      if (this.suggestedContents) {
        this.suggestedContents.forEach((content) => {
          if (
            !contentCollection ||
            !content.tags.includes(contentCollection.name)
          ) {
            content.isSuggested = true
            content.tagName = content.tags?.find((tag) =>
              this.collections.some(
                (collection) =>
                  collection.i18n[this.$voicer.locale].title === tag
              )
            )
            this.otherContents.push(content)
          }
          suggestedCollectionIds = suggestedCollectionIds.concat(content.tags)
        })
      }
      // collections non suggéré et pas celle du contenu actuel
      let otherCollections = []
      if (contentCollection) {
        otherCollections = this.collections
          .filter(
            (coll) =>
              coll.tagId !== contentCollection.id &&
              !suggestedCollectionIds.includes(coll.tagId)
          )
          .slice(0, 3 - this.otherContents.length)
      } else {
        otherCollections = this.collections.slice(
          0,
          3 - this.otherContents.length
        )
      }
      const promises = otherCollections.map(async (collection) => {
        const response = await spoke.http.get(`/contents`, {
          params: {
            limit: 1,
            is: 'podcast', // TYPES
            tags: [collection.tagId],
          },
        })
        return { items: response.items, collection }
      })
      const res = await Promise.all(promises)
      res.forEach(({ items, collection }) => {
        items.forEach((item) => {
          item.tagName = collection.i18n[this.$voicer.locale].title
          this.otherContents.push(item)
        })
      })
    },
  },
}
</script>

<style scoped>
.card {
  position: relative;

  .hooper {
    position: relative;
    height: auto !important;
  }

  .c-slidenext {
    position: absolute;
    right: -10px;
    top: 0;
    bottom: 0;
    z-index: 20;
  }

  .c-slideprev {
    position: absolute;
    left: -10px;
    top: 0;
    bottom: 0;
    z-index: 20;
  }

  .slide {
    .img {
      border-radius: 12px;
      max-height: 240px;
      object-fit: scale-down;
    }
  }

  &__description {
    height: auto;
    margin: 0 15px 15px 15px;
  }
}

.borderlines {
  margin: 0 22px;
  text-align: center;
  .b-borderline {
    display: inline-block;
    width: 75px;
    &__line {
      border-radius: 10px;
    }
  }
}
.seeMore {
  display: flex;
  justify-content: space-between;
  padding-left: 30px;
  padding-right: 30px;
  font-family: 'rubik';
  font-size: 14px !important;
  font-weight: 500 !important;
  div {
    cursor: pointer;
    text-decoration: underline;
    text-transform: uppercase;
  }
}
@media (max-width: 600px) {
  .seeMore {
    padding-left: 30px;
    padding-right: 30px;
  }
}
.scroll-container {
  display: flex;
  justify-content: space-evenly;
  overflow-x: auto;
  padding: 10px 0;
  .fixed-size-card {
    border-radius: 6px;
    min-width: 160px;
    max-width: 160px;
    margin: 0 3px;
    cursor: pointer;
    justify-content: center;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    .img-card {
      max-width: 140px !important;
      max-height: 140px;
      border-radius: 10px;
    }
    .card-title {
      font-family: 'rubik';
      font-size: 14px;
      font-weight: 500 !important;
    }
    p {
      text-align: start;
    }
    .suggested-chip {
      position: absolute;
      top: 15px;
      left: 5px;
      z-index: 2;
    }
    .contentMedatas {
      font-family: 'rubik';
      text-transform: uppercase;
      display: flex;
      justify-content: space-between;
      .contentMedatas-publishedAt {
        font-size: 11px !important;
        font-weight: 400 !important;
        padding: 4px 8px 4px 8px;
        border-radius: 3px;
      }
      .contentMedatas-duration {
        font-size: 11px !important;
        font-weight: 400 !important;
      }
    }
    .tag-name {
      background-color: #939393;
      font-family: 'rubik';
      padding: 4px 8px 4px 8px;
      border-radius: 3px;
      font-size: 11px;
      text-transform: uppercase;
    }
  }
}
</style>
